import { Component,
  Prop,
  Vue,
  Watch } from 'vue-property-decorator';
import { cloneDeep, startCase, values } from 'lodash';
import { id } from '@/support/String';
import { ValidationRules, Rules, Rule } from '@/support/ValidationRules';
import { arrayToKeyValuePair } from '@/support/Array';
import { MiButtonOptions } from '@/components/MiButton/MiButton';
import { object as dotObject } from 'dot-object';
import FileToBase64 from '@/support/FileToBase64';
import { Compact } from 'vue-color';
import { pickerColors } from '@/support/Color';
import { Model } from '@/models/Model';
import { debounce } from 'debounce';
import MiPill from '@/components/MiPill.vue';
import Datepicker from '@/components/Datepicker/Datepicker.vue';
import Rangepicker from '@/components/Rangepicker/Rangepicker.vue';

@Component<MiForm>({
  components: {
    Compact,
    MiPill,
    Datepicker,
    Rangepicker,
  },
})
export default class MiForm extends Vue {
  @Prop()
  protected fields!: MiFormField[];

  @Prop()
  protected options!: MiFormOptions;

  protected draftFields: MiFormField[] = [];

  // protected dateFormattersTable: DateFormatterTable = {};

  // protected dateMenuTable: DateMenuTable = {};

  protected debounceSearchTable: DebounceSearchTable = {};

  protected isLoaded = false;

  protected validationRules: Rules = ValidationRules();

  protected buttonOptions: MiButtonOptions | null = null;

  protected valid = false;

  protected pickerColors = pickerColors;

  protected paul: any = [false];

  protected textFields = [
    'text',
    'email',
    'password',
    'number',
    'time',
    'timeWithSeconds',
  ];

  protected debouncedSearch: (query: string, field: MiFormField) => Promise<void> = debounce(this.handleSearch, 300)

  protected debounceTextChange: (field: MiFormField) => Promise<void> = debounce(this.handleDebouncedEmit, 300)

  protected startCase: () => string = startCase;

  protected mounted(): void {
    if (this.options && this.options.buttonOptions) {
      this.buttonOptions = {
        ...{ disabled: ! this.valid },
        ...this.options.buttonOptions,
      };
    }

    this.initialize();
  }

  /**
   * Initializes the MiForm
   *
   * @memberof MiForm
   */
  public initialize(): void {
    // reset draftField
    this.draftFields = [];
    // for each field, clone that field and generate a elementId
    this.fields.forEach((field: MiFormField) => {
      const draftField = cloneDeep(field);
      draftField.id = id();

      // if (draftField.type === 'date' || draftField.type === 'month') {
      //   this.createDateFormatter(draftField);
      //   // set the field.key in dateMenuTable so you can bind the v-menu
      //   this.dateMenuTable[draftField.key] = false;
      // }

      if (draftField.type === 'autocomplete') {
        this.debounceSearchTable[draftField.key] = '';
      }

      this.draftFields.push(draftField);
    });

    // when operation is done set isLoaded to true
    this.isLoaded = true;
  }

  /**
   * Creates an instance of { DateFormatter } or a date or month field
   *
   * @protected
   * @param {MiFormField} field
   * @returns { void }
   * @memberof MiForm
   */
  // protected createDateFormatter(field: MiFormField) {
  //   if (field.type === 'date') {
  //     this.dateFormattersTable[field.key] = new DateFormatter();
  //   } else {
  //     this.dateFormattersTable[field.key] = new MonthFormatter();
  //   }

  //   if (field.value) {
  //     this.dateFormattersTable[field.key].selectedDate = field.value as string;
  //   }
  // }

  protected getTranslation(key: string): string {
    const keys: {[key: string]: string} = {
      lowercase: 'One lowercase letter required',
      uppercase: 'One uppercase letter required',
      length: '8 characters minimum',
      number: 'One number required',
      special: 'One special character required',
      match: 'The passwords must match',
    };

    return keys[key];
  }

  protected getPasswordRules(): Rule[] {
    return [
      this.validationRules.lowercase,
      this.validationRules.uppercase,
      this.validationRules.length,
      this.validationRules.numberPassword,
      this.validationRules.special,
    ];
  }

  // gets the validation rules for a particular field
  protected fieldRules(field: MiFormField) {
    if (! field.rules) {
      return [];
    }

    let rules: Function[] = [];
    field.rules.forEach((rule: string) => {
      if (rule === 'password') {
        // when a password rule is found set the proper validation rules
        rules = [...rules, ...this.getPasswordRules()];
      } else {
        rules.push(this.validationRules[rule]);
      }
    });
    return rules;
  }

  /**
   * Handles and debounces text field changes
   *
   * @protected
   * @param {MiFormField} field
   * @returns { void }
   * @memberof MiForm
   */
  protected handleTextDebounce(field: MiFormField): void {
    // this is probably not needed
    // if (! field.debounced) { return; }

    // debounce
    this.debounceTextChange(field);
  }

  /**
   * * Handles and debounces autocomplete search changes
   *
   * @protected
   * @param {string} searchInput
   * @param {MiFormField} field
   * @returns { void }
   * @memberof MiForm
   */
  protected handleAutocompleteChange(searchInput: string, field: MiFormField): void {
    if (! searchInput || searchInput.length === 0) {
      return;
    }
    if (this.debounceSearchTable[field.key] === searchInput) {
      return;
    }

    // debounce
    this.debouncedSearch(searchInput, field);
  }

  /**
   * Handles whenever a date / month field is cleared
   * and emits to the parent
   *
   * @protected
   * @param {MiFormField} field
   * @returns { void }
   * @memberof MiForm
   */
  protected handleDateClear(field: MiFormField): void {
    // eslint-disable-next-line
    field.value = '';
    this.$emit('fieldChanged', field);
  }

  /**
   * Emits the debouced text field change
   *
   * @protected
   * @param {MiFormField} field
   * @returns { void }
   * @memberof MiForm
   */
  protected async handleDebouncedEmit(field: MiFormField): Promise<void> {
    if (this.options.emitFullFormOnChange) {
      const data = await this.parseFromData();
      this.$emit('fieldChanged', data);
    } else {
      this.$emit('fieldChanged', field);
    }
  }

  /**
   * Handles the debouced search on an autocomplete field
   *
   * @protected
   * @param {string} query
   * @param {MiFormField} field
   * @returns { void }
   * @memberof MiForm
   */
  protected async handleSearch(query: string, field: MiFormField): Promise<void> {
    if (! field.model) { return; }

    // eslint-disable-next-line
    field.isLoading = true;
    let items: Model[] = [];
    const results = await field.model.filter('search', query).all();

    if (field.value && field.type === 'autocomplete') {
      if (field.multiple) {
        items = [...field.value as Record<string, any>[], ...results];
      } else {
        items = [...[field.value], ...results];
      }
    } else {
      items = results;
    }

    this.$set(field, 'items', items);

    // eslint-disable-next-line
    field.isLoading = false;
  }

  /**
   * Method is called when the submit button is clicked
   * retrieves all values from the form fields, parses them to an object
   * and emits the parsed data
   *
   *
   * @protected
   * @returns { void }
   * @memberof MiForm
   */
  protected async handleFormSubmit(): Promise<void> {
    if (! this.valid) { return; }
    const data = await this.parseFromData();

    this.$emit(
      'submit',
      data,
    );
  }

  /**
   * Method is called when the enter key is clicked
   * retrieves all values from the form fields, parses them to an object
   * and emits the parsed data
   *
   *
   * @protected
   * @returns { void }
   * @memberof MiForm
   */
  protected async handleFormSubmitOnKeyEnter(): Promise<void> {
    if (! this.valid) { return; }
    const data = await this.parseFromData();

    this.$emit(
      'keyEnter',
      data,
    );
  }

  /**
   * Handles a field changed events and emits either the field
   * or the complete form
   *
   * @protected
   * @param {MiFormField} field
   * @returns { void }
   * @memberof MiForm
   */
  protected async handleFieldChanged(field: MiFormField): Promise<void> {
    if (field.debounced) { return; }

    if (field.conditional) {
      this.$emit('conditionalChanged', field);
    }

    // if (field.type === 'date' || field.type === 'month') {
    //   // eslint-disable-next-line
    //   field.value = this.dateFormattersTable[field.key].selectedDate;
    // }

    if (this.options.emitFullFormOnChange) {
      const data = await this.parseFromData();
      this.$emit('fieldChanged', data);
    } else {
      this.$emit('fieldChanged', field);
    }
  }

  protected removeItemFromAutoComplete(item: string | Record<string, any>, field: MiFormField): void {
    if (! field.returnObject) {
      field.value = (field.value as string[]).filter((value: string) => item !== value);
    } else {
      field.value = (field.value as Record<string, any>[])
        .filter((record: Record<string, any>) => (item as Record<string, any>)[field.itemValue || 'id'] !== record[field.itemValue || 'id']);
    }
  }

  /**
   * Retrieves the values of every field in the form
   * Parses data and returns an key, value object
   *
   * @returns { object }
   * @memberof MiForm
   */
  public async parseFromData() {
    const fileFields = this.draftFields.filter((draftField: MiFormField) => draftField.type === 'file');

    for (let index = 0; index < fileFields.length; index += 1) {
      if (fileFields[index] && fileFields[index].value) {
        // eslint-disable-next-line
        const base64 = await this.parseFileToBase64(fileFields[index]);
        fileFields[index].value = base64;
      }
    }

    const colorFields = this.draftFields.filter((draftField: MiFormField) => draftField.type === 'colorpicker');
    for (let index = 0; index < colorFields.length; index += 1) {
      colorFields[index].value = (colorFields[index].value as any).hex;
    }

    const fields = this.draftFields.filter((draftField: MiFormField) => draftField.type !== 'label');

    // eslint-disable-next-line
    const data: { [key: string]: any } = dotObject(
      arrayToKeyValuePair(
        // eslint-disable-next-line
        fields as any[],
        'key',
        'value',
      ),
    );
    return data;
  }

  public setValue(key: string, value: string): void {
    const field = this.draftFields.find((draftField: MiFormField) => draftField.key === key);
    if (! field) { return; }

    this.$set(field, 'value', value);
    this.$emit('fieldChanged', field);
  }

  public toggleField(key: string, hidden: boolean): void {
    const field = this.draftFields.find((draftField: MiFormField) => draftField.key === key);
    if (! field) { return; }

    this.$set(field, 'hidden', hidden);
  }

  public disableFields(keys: string[], hidden: boolean): void {
    keys.forEach((key: string) => {
      const field = this.draftFields.find((draftField: MiFormField) => draftField.key === key);
      if (! field) { return; }

      this.$set(field, 'disabled', hidden);
    });
  }

  public isFormValid(): boolean {
    return this.valid;
  }

  public async isFormValidWithValidation(): Promise<boolean> {
    await this.$nextTick();
    // eslint-disable-next-line
    return await (this.$refs.form as any).validate();
  }

  public clearFieldByKey(key: string): void {
    if (! key) { return; }

    const field = this.draftFields.find((draftField: MiFormField) => draftField.key === key);
    if (! field) { return; }

    field.value = '';
  }

  public clearValueFormFieldByKey(key: string): void {
    if (! key) { return; }

    const field = this.draftFields.find((draftField: MiFormField) => draftField.key === key);
    if (! field) { }
  }

  public refreshFields(): void {
    this.initialize();
  }

  /**
   * Convert a file to a base64 string
   *
   * @private
   * @param {MiFormField} field
   * @returns { string }
   * @memberof MiForm
   */
  private async parseFileToBase64(field: MiFormField) {
    const base64 = await FileToBase64(field.value as File);
    return base64;
  }

  protected get lazyValidation(): boolean {
    if (! this.options || this.options.lazyValidation === undefined) {
      return false;
    }

    return this.options.lazyValidation;
  }

  protected get form(): any {
    return this.$refs.form as any;
  }

  protected get colors(): string[] {
    return pickerColors;
  }

  protected get filteredDraftFields(): MiFormField[] {
    return this.draftFields.filter((draftField: MiFormField) => ! draftField.hidden);
  }

  protected get isMenuOpen(): boolean {
    return this.paul[0];
  }

  protected set isMenuOpen(value: boolean) {
    this.paul[0] = value;
  }

  @Watch('valid')
  protected validChanged(): void {
    this.$emit('validationChanged', this.valid);
    if (this.buttonOptions) {
      this.$set(this.buttonOptions, 'disabled', ! this.valid);
    }
  }
}

export interface MiFormData {
  [key: string]: string;
}

export interface MiFormOptions {
  buttonOptions?: MiButtonOptions; // Sets the MiButtonOptions for the submit button
  buttonAlignment?: MiFormButtonAlignment; // 'left' | 'center' | 'right'. Sets the alignment of the submit button
  lazyValidation?: boolean; // Defaults to true. When set to false the form checks validation on every field changes
  theme?: MiFormTheme; // 'default' | 'light' | 'dark'. Sets the appearance of the form
  emitFullFormOnChange?: boolean; // Defaults to false. When set to true the form emits the full key,value object on field changes
}

export interface MiFormField {
  // TODO: create a Typescript type for the type property
  /**
   * Sets the type of the form field
   * text' | 'email' | 'password' | 'number' | 'time' | 'textarea' |
   * 'colorpicker' | 'radio' | 'checkbox' | 'file' | 'select' | 'autocomplete' |
   * 'switch' | 'label' | 'date' | 'month'
   */
  type: string;
  /**
   * sets the key of the field
   * this key is also used is the key on the data object the is being emitted when the form
   * is submitted
   * This key can contain dot and array notation which is properly parsed in the data object
   * ex.
   * 'user_name' => { user_name: value}
   * 'user.name' => { user: { name: value } }
   * 'user[0].name' => { user: [ { name: value } ] }
   */
  key: string;
  id?: string;
  placeholder?: string;
  label?: string;
  prependIcon?: string;
  appendIcon?: string;
  prependInnerIcon?: string;
  appendOuterIcon?: string;
  clearable?: boolean;
  clearIcon?: string;
  disabled?: boolean;
  prefix?: string;
  suffix?: string;
  value?: string | string [] | Record<string, any> | Record<string, any>[] | File | boolean | object | null;
  hint?: string;
  min?: string;
  max?: string;
  hideDetails?: boolean;
  rules?: string[];
  options?: MiFormFieldOption[];
  items?: { [key: string]: any }[] | null;
  class?: string;
  conditional?: boolean;
  itemText?: string;
  itemValue?: string;
  multiple?: boolean;
  model?: Model;
  isLoading?: boolean;
  hidden?: boolean;
  collapsed?: boolean;
  debounced?: boolean;
  tooltip?: string;
  returnObject?: boolean;
  trueLabel?: string;
  falseLabel?: string;
  createNewText?: string,
  rows?: string | number,
  createMethod?: () => void,
}

export interface MiFormFieldOption {
  label?: string;
  value?: string;
}

// export interface DateFormatterTable {
//   [key: string]: DateFormatter | MonthFormatter;
// }

// export interface DateMenuTable {
//   [key: string]: boolean;
// }

export interface DebounceSearchTable {
  [key: string]: string;
}

export type MiFormTheme = 'default' | 'dark' | 'light'
export type MiFormButtonAlignment = 'left' | 'center' | 'right';

export type MiFormFieldSelectItem = {
  label: string;
  value: string;
}
